import React, { useState, useEffect } from 'react';
import { 
  ExternalLink, 
  Copy, 
  Check, 
  Eye, 
  EyeOff,
  AlertCircle,
  Monitor,
  Key,
  User,
  Mail
} from 'lucide-react';
import { useApiCall } from '../../hooks/useApi';
import { userAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const MyConsole = () => {
  const { notify } = useNotificationHook();
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Fetch user profile to check if CRM is enabled
  const { data: profileResponse, loading: profileLoading, error: profileError } = useApiCall(
    () => userAPI.getProfile(),
    [],
    true
  );

  const user = profileResponse?.user || profileResponse?.data?.user;
  
  console.log('MyConsole: profileResponse:', profileResponse);
  console.log('MyConsole: profileResponse structure:', {
    hasData: !!profileResponse?.data,
    hasUser: !!profileResponse?.user,
    hasDataUser: !!profileResponse?.data?.user,
    userFromData: profileResponse?.data?.user,
    userFromRoot: profileResponse?.user
  });
  console.log('MyConsole: profileError:', profileError);
  console.log('MyConsole: user:', user);
  console.log('MyConsole: user?.crm_enabled:', user?.crm_enabled);

  // Fetch CRM credentials if CRM is enabled
  const { data: crmResponse, loading: crmLoading, error: crmError } = useApiCall(
    () => userAPI.getCrmCredentials(),
    [],
    user?.crm_enabled
  );

  const crmCredentials = crmResponse || crmResponse?.data;
  
  console.log('MyConsole: crmResponse:', crmResponse);
  console.log('MyConsole: crmError:', crmError);
  console.log('MyConsole: crmCredentials:', crmCredentials);

  const handleCrmLogin = () => {
    window.open('https://app.niswainfotech.com/authentication/login', '_blank');
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      notify.success(`${fieldName} copied to clipboard!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      notify.error('Failed to copy to clipboard');
    }
  };

  const getCopyIcon = (fieldName) => {
    if (copiedField === fieldName) {
      return <Check className="h-5 w-5 text-green-600" />;
    }
    return <Copy className="h-5 w-5" />;
  };

  if (profileLoading) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8">
        <div className="animate-pulse">
          <div className="h-6 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/3 mb-4 sm:mb-6"></div>
          <div className="space-y-3 sm:space-y-4">
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4"></div>
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user?.crm_enabled) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-6 sm:p-8 md:p-12">
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg">
            <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            CRM Access Not Available
          </h3>
          <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mb-6 sm:mb-8">
            CRM access has not been enabled for your account. Please contact your administrator to enable CRM access.
          </p>
        </div>
      </div>
    );
  }

  if (crmLoading) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8">
        <div className="animate-pulse">
          <div className="h-6 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/3 mb-4 sm:mb-6"></div>
          <div className="space-y-3 sm:space-y-4">
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/2"></div>
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-3/4"></div>
            <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-2xl w-1/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (crmError) {
    return (
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-6 sm:p-8 md:p-12">
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
            <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Error Loading CRM Credentials
          </h3>
          <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400">
            {crmError.response?.data?.message || 'Failed to load CRM credentials. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-4 sm:p-6 md:p-8">
        <div className="flex items-center mb-4 sm:mb-6">
          <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4">
            <Monitor className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              My Console
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400">
              Access your external CRM system
            </p>
          </div>
        </div>

        {/* CRM Login Button */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={handleCrmLogin}
            className="w-full card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base touch-manipulation"
          >
            <ExternalLink className="h-5 w-5 sm:h-6 sm:w-6" />
            Login to CRM System
          </button>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mt-2 sm:mt-3 text-center">
            Opens in a new tab
          </p>
        </div>

        {/* CRM Credentials */}
        <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-6 sm:pt-8">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4">
              <Key className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            CRM Login Details
          </h3>
          
          <div className="space-y-4 sm:space-y-6">
            {/* Email */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4 flex-shrink-0">
                    <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="text-sm sm:text-base font-bold text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-white font-mono truncate">
                      {crmCredentials?.crm_email || 'Not provided'}
                    </p>
                  </div>
                </div>
                {crmCredentials?.crm_email && (
                  <button
                    onClick={() => copyToClipboard(crmCredentials.crm_email, 'Email')}
                    className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ml-2"
                    title="Copy email"
                  >
                    {getCopyIcon('Email')}
                  </button>
                )}
              </div>
            </div>

            {/* Username */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4 flex-shrink-0">
                    <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="text-sm sm:text-base font-bold text-gray-700 dark:text-gray-300">
                      Username
                    </label>
                    <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-white font-mono truncate">
                      {crmCredentials?.crm_username || 'Not provided'}
                    </p>
                  </div>
                </div>
                {crmCredentials?.crm_username && (
                  <button
                    onClick={() => copyToClipboard(crmCredentials.crm_username, 'Username')}
                    className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ml-2"
                    title="Copy username"
                  >
                    {getCopyIcon('Username')}
                  </button>
                )}
              </div>
            </div>

            {/* Password */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4 flex-shrink-0">
                    <Key className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="text-sm sm:text-base font-bold text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <div className="flex items-center">
                      <p className="text-sm sm:text-base md:text-lg text-gray-900 dark:text-white font-mono truncate">
                        {showPassword 
                          ? (crmCredentials?.crm_password || 'Not provided')
                          : (crmCredentials?.crm_password ? '••••••••' : 'Not provided')
                        }
                      </p>
                      {crmCredentials?.crm_password && (
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="ml-2 sm:ml-3 p-1 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation"
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {crmCredentials?.crm_password && (
                  <button
                    onClick={() => copyToClipboard(crmCredentials.crm_password, 'Password')}
                    className="p-2 sm:p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 rounded-xl sm:rounded-2xl transition-all duration-300 touch-manipulation ml-2"
                    title="Copy password"
                  >
                    {getCopyIcon('Password')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 sm:mt-8 card-3d bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-xl border border-yellow-200 dark:border-yellow-800 p-4 sm:p-6 md:p-8">
            <div className="flex items-start">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg mr-3 sm:mr-4 flex-shrink-0">
                <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <h4 className="text-base sm:text-lg font-bold text-yellow-800 dark:text-yellow-200 mb-1 sm:mb-2">
                  Security Notice
                </h4>
                <p className="text-sm sm:text-base text-yellow-700 dark:text-yellow-300">
                  Keep your CRM credentials secure and do not share them with others. These credentials provide access to your business data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyConsole;
